if (!customElements.get('product-modal')) {
  customElements.define('product-modal', class ProductModal extends ModalDialog {
    constructor() {
      super();
      this.addEventListener('click', event => {
        const direction = event.target.closest('[data-modal-direction]');
        if (direction) this.step(Number(direction.dataset.modalDirection));
        else if (event.target === this.querySelector('[role="dialog"]') || event.target === this.querySelector('[role="document"]')) this.hide();
      });
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.hide(); }
        else if (event.key === 'Tab') {
          const buttons = [...this.querySelectorAll('button:not([disabled]), [tabindex="0"]')].filter(node => node.getClientRects().length && !node.closest('[hidden]'));
          if (!buttons.length) return;
          event.preventDefault(); event.stopPropagation();
          const index = buttons.indexOf(document.activeElement);
          buttons[(index + (event.shiftKey ? -1 : 1) + buttons.length) % buttons.length].focus();
        } else if (!event.target.closest('[data-room-sizes]') && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
          event.preventDefault(); this.step(event.key === 'ArrowLeft' ? -1 : 1);
        }
      }, true);
      this.pointers = new Map();
      this.zoom = 1; this.panX = 0; this.panY = 0;
      this.addEventListener('pointerdown', event => this.pointerDown(event));
      this.addEventListener('pointermove', event => this.pointerMove(event));
      this.addEventListener('pointerup', event => this.pointerEnd(event));
      this.addEventListener('pointercancel', event => this.pointerEnd(event, true));
      this.addEventListener('click', event => {
        if (performance.now() < (this.suppressClickUntil || 0) && !event.target.closest('button')) {
          event.preventDefault(); event.stopImmediatePropagation();
        }
      }, true);
      this.onResize = () => { this.resetZoom(); this.fitMedia(); };
    }
    touchZoomEnabled() {
      return window.matchMedia('(max-width: 1023.98px)').matches && navigator.maxTouchPoints > 0;
    }
    zoomMedia() {
      return this.activeId === 'room-guide' ? this.roomGuide?.querySelector('.mc-room-scene') : this.querySelector('[role="document"] > img.active');
    }
    resetZoom() {
      if (this.zoomTarget) this.zoomTarget.style.removeProperty('transform');
      this.zoomTarget = null;
      this.zoom = 1; this.panX = 0; this.panY = 0;
      this.pointers?.clear(); this.gesture = null; this.lastTap = null; this.swipeStart = null;
      this.dataset.zoom = '1';
    }
    applyZoom(scale, x, y) {
      const target = this.zoomMedia();
      if (!target) return;
      this.zoomTarget = target;
      this.zoom = Math.max(1, Math.min(4, scale));
      const viewport = target.closest('.mc-room-view') || this.querySelector('[role="document"]');
      const box = target.getBoundingClientRect();
      const previousScale = Number(this.dataset.zoom) || 1;
      const frame = viewport.getBoundingClientRect();
      const limitX = Math.max(0, (box.width / previousScale * this.zoom - frame.width) / 2);
      const limitY = Math.max(0, (box.height / previousScale * this.zoom - frame.height) / 2);
      this.panX = this.zoom === 1 ? 0 : Math.max(-limitX, Math.min(limitX, x));
      this.panY = this.zoom === 1 ? 0 : Math.max(-limitY, Math.min(limitY, y));
      target.style.transform = `translate(${this.panX}px, ${this.panY}px) scale(${this.zoom})`;
      this.dataset.zoom = String(this.zoom);
      if (this.activeId === 'room-guide') this.roomGuide.renderArtwork();
    }
    zoomAround(scale, point, start = { scale: this.zoom, x: this.panX, y: this.panY }, origin = point) {
      const rect = this.zoomMedia().getBoundingClientRect();
      const cx = rect.left + rect.width / 2 - this.panX, cy = rect.top + rect.height / 2 - this.panY;
      const next = Math.max(1, Math.min(4, scale));
      this.applyZoom(next, point.x - cx - (origin.x - cx - start.x) * next / start.scale,
        point.y - cy - (origin.y - cy - start.y) * next / start.scale);
    }
    beginGesture() {
      const points = [...this.pointers.values()];
      this.gesture = { scale: this.zoom, x: this.panX, y: this.panY,
        point: points.length > 1 ? { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 } : points[0],
        distance: points.length > 1 ? Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y) : 0 };
    }
    pointerDown(event) {
      if (event.target.closest('button, [data-room-sizes]')) return;
      const target = this.zoomMedia();
      if (event.pointerType !== 'touch' || !this.touchZoomEnabled() || !target?.contains(event.target)) {
        this.swipeStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
        return;
      }
      event.preventDefault();
      this.setPointerCapture(event.pointerId);
      const point = { x: event.clientX, y: event.clientY };
      if (!this.pointers.size) this.touchStart = { ...point, time: performance.now(), moved: false, multi: false, zoomed: this.zoom > 1 };
      this.pointers.set(event.pointerId, point);
      if (this.pointers.size > 1) { this.touchStart.multi = true; this.lastTap = null; }
      this.beginGesture();
    }
    pointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      event.preventDefault();
      const point = { x: event.clientX, y: event.clientY };
      this.pointers.set(event.pointerId, point);
      if (Math.hypot(point.x - this.touchStart.x, point.y - this.touchStart.y) > 8) this.touchStart.moved = true;
      const points = [...this.pointers.values()], start = this.gesture;
      if (points.length > 1) {
        const midpoint = { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 };
        const distance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
        if (start.distance > 0) this.zoomAround(start.scale * distance / start.distance, midpoint, start, start.point);
      } else if (this.zoom > 1) this.applyZoom(this.zoom, start.x + point.x - start.point.x, start.y + point.y - start.point.y);
    }
    pointerEnd(event, cancelled = false) {
      if (!this.pointers.has(event.pointerId)) {
        const start = this.swipeStart; this.swipeStart = null;
        if (cancelled || !start || start.id !== event.pointerId || this.zoom > 1) return;
        const dx = event.clientX - start.x, dy = event.clientY - start.y;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) this.step(dx < 0 ? 1 : -1);
        return;
      }
      event.preventDefault();
      this.pointers.delete(event.pointerId);
      if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
      this.suppressClickUntil = performance.now() + 450;
      if (cancelled) { this.touchStart.multi = true; this.lastTap = null; }
      if (this.pointers.size) { this.beginGesture(); return; }
      const start = this.touchStart, now = performance.now();
      if (!cancelled && !start.multi) {
        if (!start.moved && now - start.time < 350) {
          const point = { x: event.clientX, y: event.clientY };
          if (this.lastTap && now - this.lastTap.time < 350 && Math.hypot(point.x - this.lastTap.x, point.y - this.lastTap.y) < 30) {
            this.zoomAround(this.zoom > 1 ? 1 : 2.5, point); this.lastTap = null;
          } else this.lastTap = { ...point, time: now };
        } else {
          this.lastTap = null;
          const dx = event.clientX - start.x, dy = event.clientY - start.y;
          if (!start.zoomed && this.zoom === 1 && Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) this.step(dx < 0 ? 1 : -1);
        }
      }
      this.gesture = null;
    }
    connectedCallback() {
      super.connectedCallback();
      const navigation = this.querySelector('[data-mc-modal-navigation]');
      if (navigation && !this.navigationMql) {
        this.navigationMql = matchMedia('(min-width:750px)');
        this.updateNavigation = () => {
          const focused = document.activeElement?.closest('.mc-modal-arrow');
          this.querySelectorAll('.mc-modal-arrow').forEach(button => button.remove());
          if (this.navigationMql.matches) navigation.after(navigation.content.cloneNode(true));
          else if (focused) this.querySelector('[id^="ModalClose-"]')?.focus();
        };
        this.navigationMql.addEventListener('change', this.updateNavigation);
        this.updateNavigation();
      }
      if (this.mediaObserver) return;
      this.mediaObserver = new MutationObserver(() => { if (this.hasAttribute('open')) this.showActiveMedia(); });
      this.mediaObserver.observe(this.querySelector('[role="document"]'), { childList: true });
    }
    disconnectedCallback() {
      if (this.hasAttribute('open')) this.hide();
      this.navigationMql?.removeEventListener('change', this.updateNavigation);
      this.navigationMql = null;
      this.mediaObserver?.disconnect();
      this.mediaObserver = null;
    }
    show(opener) {
      if (this.hasAttribute('open')) return;
      this.gallery = opener.closest('media-gallery');
      this.activeId = opener.dataset.mediaId;
      this.scrollPosition = { x: scrollX, y: scrollY };
      this.bodyStyle = Object.fromEntries(['position', 'top', 'left', 'width', 'overflow'].map(key => [key, document.body.style[key]]));
      this.rootOverflow = document.documentElement.style.overflow;
      this.hadOverflowHidden = document.body.classList.contains('overflow-hidden');
      this.background = [...document.body.children].filter(node => node !== this).map(node => ({ node, inert: node.inert }));
      this.background.forEach(({ node }) => { node.inert = true; });
      Object.assign(document.body.style, { position: 'fixed', top: -scrollY + 'px', left: -scrollX + 'px', width: '100%', overflow: 'hidden' });
      document.documentElement.style.overflow = 'hidden';
      super.show(opener);
      this.showActiveMedia();
      this.querySelector('[id^="ModalClose-"]').focus({ preventScroll: true });
      window.addEventListener('resize', this.onResize);
    }
    hide() {
      if (!this.hasAttribute('open')) return;
      this.resetZoom();
      this.removeAttribute('open');
      window.removeEventListener('resize', this.onResize);
      this.roomGuide?.remove(); this.roomGuide = null;
      this.background?.forEach(({ node, inert }) => { node.inert = inert; });
      Object.assign(document.body.style, this.bodyStyle);
      document.documentElement.style.overflow = this.rootOverflow;
      if (!this.hadOverflowHidden) document.body.classList.remove('overflow-hidden');
      removeTrapFocus();
      window.pauseAllMedia();
      window.scrollTo(this.scrollPosition.x, this.scrollPosition.y);
      this.openedBy?.focus({ preventScroll: true });
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
    }
    mediaOrder() {
      if (!this.gallery) return [this.activeId];
      return [...this.gallery.querySelectorAll('.product__media-list > [data-media-id]')]
        .filter(node => !node.hidden)
        .map(node => node.dataset.mediaId.replace(this.gallery.id.replace('MediaGallery-', '') + '-', ''))
        .filter(id => id === 'room-guide' || this.querySelector('[role="document"] [data-media-id="' + id + '"]'));
    }
    step(direction) {
      const order = this.mediaOrder();
      this.activeId = order[(Math.max(0, order.indexOf(this.activeId)) + direction + order.length) % order.length];
      window.pauseAllMedia();
      this.showActiveMedia();
    }
    showActiveMedia() {
      if (this.dataset.activeMedia !== this.activeId || (this.zoomTarget && !this.zoomTarget.isConnected)) this.resetZoom();
      const content = this.querySelector('[role="document"]');
      const host = this.querySelector('[data-room-modal-host]');
      const isRoom = this.activeId === 'room-guide';
      content.hidden = isRoom; host.hidden = !isRoom;
      content.querySelectorAll('[data-media-id]').forEach(node => node.classList.toggle('active', node.dataset.mediaId === this.activeId));
      if (isRoom && !this.roomGuide) {
        const original = this.gallery.querySelector('mc-room-guide');
        if (!original) return;
        const guide = original.cloneNode(true);
        guide.gallery = this.gallery;
        guide.productInfo = original.productInfo;
        guide.enlarged = true;
        guide.dataset.mediaId = this.gallery.id.replace('MediaGallery-', '') + '-room-guide';
        delete guide.dataset.imagesRequested;
        delete guide.dataset.naturalWidth;
        guide.querySelector('[data-room-open]')?.remove();
        guide.querySelectorAll('img, source').forEach(image => ['src', 'srcset', 'sizes'].forEach(attribute => image.removeAttribute(attribute)));
        this.roomGuide = guide;
        host.replaceChildren(guide);
      }
      if (isRoom) this.roomGuide?.sync();
      else content.querySelector('.active')?.loadContent?.();
      const order = this.mediaOrder();
      this.querySelector('[data-modal-status]').textContent = (order.indexOf(this.activeId) + 1) + ' / ' + order.length;
      this.dataset.activeMedia = this.activeId;
      this.fitMedia();
    }
    fitMedia() {
      const dpr = window.devicePixelRatio || 1;
      this.style.setProperty('--mc-room-max-width', 1600 / dpr + 'px');
      this.querySelectorAll('[data-full-width]').forEach(image => image.style.setProperty('--mc-source-max-width', Number(image.dataset.fullWidth) / dpr + 'px'));
      this.roomGuide?.renderArtwork();
      if (this.zoom > 1) this.applyZoom(this.zoom, this.panX, this.panY);
    }
  });
}
